import React from "react";
import { Sparkles, BarChart3, Bot, Trophy, PawPrint, Layers, Trophy as TrophyIcon, ExternalLink, Share2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { trackEvent } from "@/utils/analyticsTrack";

const TOOL_META: Record<string, { label: string; route: string; icon: React.ComponentType<{ className?: string }> }> = {
  "gerar-ia": { label: "Gerar palpites com IA", route: "/gerar-jogos", icon: Sparkles },
  estatisticas: { label: "Ver Estatísticas", route: "/estatisticas", icon: BarChart3 },
  "estatisticas-rio": { label: "Estatísticas Rio", route: "/estatisticas", icon: BarChart3 },
  "estatisticas-federal": { label: "Estatísticas Federal", route: "/estatisticas", icon: BarChart3 },
  metodos: { label: "Ver Métodos", route: "/metodos", icon: Layers },
  resultados: { label: "Resultados ao vivo", route: "/resultados-bicho", icon: TrophyIcon },
  puxadas: { label: "Puxadas", route: "/puxadas", icon: PawPrint },
  loterias: { label: "Loterias Caixa", route: "/loterias", icon: Trophy },
};

interface ActionData { tool: string; label?: string; route?: string; reason?: string; }
interface PalpiteData { grupos?: number[]; dezenas?: string[]; milhar?: string; }

export const ActionCard: React.FC<{ data: ActionData }> = ({ data }) => {
  const navigate = useNavigate();
  const meta = TOOL_META[data.tool] || { label: data.label || "Abrir ferramenta", route: data.route || "/vitrine", icon: Bot };
  const Icon = meta.icon;
  return (
    <div className="mt-3 rounded-lg border border-gold-500/30 bg-gradient-to-br from-card to-card/60 p-3">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-gold-500/15 p-2"><Icon className="h-4 w-4 text-gold-500" /></div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-foreground">{meta.label}</div>
          {data.reason && <div className="text-xs text-muted-foreground mt-0.5">{data.reason}</div>}
        </div>
      </div>
      <Button
        size="sm"
        className="mt-3 w-full bg-gradient-to-r from-gold-600 to-gold-500 text-card hover:from-gold-700 hover:to-gold-600"
        onClick={() => {
          trackEvent("guru_chat_tool_action" as any, { tool: data.tool, route: meta.route });
          navigate(meta.route);
        }}
      >
        <ExternalLink className="h-3.5 w-3.5 mr-1" />
        Abrir agora
      </Button>
    </div>
  );
};

export const PalpiteCard: React.FC<{ data: PalpiteData }> = ({ data }) => {
  const grupos = data.grupos ?? [];
  const dezenas = data.dezenas ?? [];
  const milhar = data.milhar ?? null;

  const shareText = `🔮 Palpite do Guru Chat — IA Palpite Guru\n${grupos.length ? `Grupos: ${grupos.join(", ")}\n` : ""}${dezenas.length ? `Dezenas: ${dezenas.join(", ")}\n` : ""}${milhar ? `Milhar: ${milhar}\n` : ""}\nGere os seus em: https://aipalpiteguru.mentepoderosa2030.com`;

  const handleShare = () => {
    trackEvent("guru_chat_tool_action" as any, { tool: "share_palpite" });
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleDownload = () => {
    trackEvent("guru_chat_tool_action" as any, { tool: "download_palpite" });
    const canvas = document.createElement("canvas");
    canvas.width = 800; canvas.height = 600;
    const ctx = canvas.getContext("2d")!;
    // Background
    const grad = ctx.createLinearGradient(0, 0, 800, 600);
    grad.addColorStop(0, "#0b0b0f"); grad.addColorStop(1, "#1a1413");
    ctx.fillStyle = grad; ctx.fillRect(0, 0, 800, 600);
    // Title
    ctx.fillStyle = "#d4af37"; ctx.font = "bold 36px sans-serif"; ctx.textAlign = "center";
    ctx.fillText("Guru Chat — Palpite", 400, 80);
    ctx.fillStyle = "#9aa0a6"; ctx.font = "18px sans-serif";
    ctx.fillText("IA Palpite Guru", 400, 110);
    // Body
    ctx.fillStyle = "#ffffff"; ctx.font = "26px sans-serif"; ctx.textAlign = "left";
    let y = 200;
    if (grupos.length) { ctx.fillText(`Grupos: ${grupos.join(", ")}`, 80, y); y += 50; }
    if (dezenas.length) { ctx.fillText(`Dezenas: ${dezenas.join(", ")}`, 80, y); y += 50; }
    if (milhar) { ctx.fillText(`Milhar: ${milhar}`, 80, y); y += 50; }
    // Footer
    ctx.fillStyle = "#d4af37"; ctx.font = "18px sans-serif"; ctx.textAlign = "center";
    ctx.fillText("aipalpiteguru.mentepoderosa2030.com", 400, 560);

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "palpite-guru-chat.png"; a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="mt-3 rounded-lg border border-gold-500/30 bg-card p-4">
      <div className="text-xs uppercase tracking-wider text-gold-500 font-bold mb-2">Palpite do Guru</div>
      <div className="space-y-1.5 text-sm">
        {grupos.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-muted-foreground">Grupos:</span>
            {grupos.map((g) => (
              <span key={g} className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md bg-gold-500/15 text-gold-500 font-bold">{g}</span>
            ))}
          </div>
        )}
        {dezenas.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            <span className="text-muted-foreground">Dezenas:</span>
            {dezenas.map((d, i) => (
              <span key={i} className="inline-flex items-center justify-center min-w-[2rem] h-7 px-2 rounded-md bg-primary/15 text-primary font-bold">{d}</span>
            ))}
          </div>
        )}
        {milhar && (
          <div><span className="text-muted-foreground">Milhar:</span> <span className="font-bold text-foreground">{milhar}</span></div>
        )}
      </div>
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" className="flex-1" onClick={handleShare}>
          <Share2 className="h-3.5 w-3.5 mr-1" /> WhatsApp
        </Button>
        <Button size="sm" variant="outline" className="flex-1" onClick={handleDownload}>
          <Download className="h-3.5 w-3.5 mr-1" /> Imagem
        </Button>
      </div>
    </div>
  );
};
